import { useState, useCallback } from 'react';
import { api_get } from '@lf/api-client/lfApi';

const DEBUG_MODE_ENABLED = process.env.REACT_APP_LF_DEBUG_MODE === '1';

// Recommended-path compute can take ~30-90s for a fresh lesson — see
// lf_api/orchestrator/recommended_path.py and the matching note in
// VideosPage.jsx. Give the call enough headroom that a healthy compute
// returns within one request; the page's poll effect only kicks in when
// the BE reports a concurrent worker already owns the row.
const RECOMMENDED_PATH_TIMEOUT_MS = 5 * 60 * 1000;

// useFeedData owns the four pieces of feed state — metadata, items,
// recommended-path, and the debug overlay — plus the fetchers that
// populate each. The page wraps the fetchers in handlers that add UX
// concerns the hook intentionally does not own (fireworks timing on
// next/prev, in-flight protection across rapid clicks, etc.).
//
// State exposed:
//   feedMetadata        — /feed/<id>          (current_position + meta)
//   feedItemsResp       — /feed/<id>/items    (raw video list)
//   recommendedPath     — /recommended-path   (rec videos + status)
//   debugFeedData       — /debug/feed         (dev-only overlay)
//   isLoadingMetadata, isLoadingItems, isRecommendedPathLoading
//   feedItems           — derived: metadata merged with items + progress
//   showFunLoader       — derived: should the FunLoader cover the feed?
export function useFeedData({ setError }) {
  const [feedMetadata, setFeedMetadata] = useState(null);
  const [feedItemsResp, setFeedItemsResp] = useState(null);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(true);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [recommendedPath, setRecommendedPath] = useState(null);
  const [isRecommendedPathLoading, setIsRecommendedPathLoading] = useState(false);
  const [debugFeedData, setDebugFeedData] = useState(null);

  const fetchRecommendedPath = useCallback(async (spId, lessonId) => {
    if (!spId || !lessonId) return;
    setIsRecommendedPathLoading(true);
    try {
      const response = await api_get(
        `/recommended-path/?study_plan_id=${spId}&lesson_id=${lessonId}`,
        null,
        setError,
        RECOMMENDED_PATH_TIMEOUT_MS,
      );
      if (response) setRecommendedPath(response.data);
    } finally {
      setIsRecommendedPathLoading(false);
    }
  }, [setError]);

  const fetchFeedItems = useCallback(async (spId, keywords) => {
    if (!spId) return;
    setIsLoadingItems(true);
    try {
      const response = await api_get(
        `/feed/${spId}/items?keywords=${keywords || ''}`,
        null,
        setError,
        120000,
      );
      if (response) setFeedItemsResp(response.data);
    } finally {
      setIsLoadingItems(false);
    }
  }, [setError]);

  const fetchDebugFeedData = useCallback(async (
    spId,
    lessonIdValue,
    topicIdValue,
    allVideoIds,
    recommendedVideoIds,
  ) => {
    if (!DEBUG_MODE_ENABLED || !spId || !lessonIdValue || !topicIdValue) {
      setDebugFeedData(null);
      return;
    }

    const response = await api_get(
      '/debug/feed',
      {
        study_plan_id: spId,
        lesson_id: lessonIdValue,
        topic_id: topicIdValue,
        all_video_ids: allVideoIds.join(','),
        recommended_video_ids: recommendedVideoIds.join(','),
      },
      setError,
      90000,
    );

    if (response) {
      setDebugFeedData(response.data);
    }
  }, [setError]);

  const fetchFeed = useCallback(async (studyPlanId, keywords) => {
    setIsLoadingMetadata(true);
    setFeedMetadata(null);
    setFeedItemsResp(null);
    setRecommendedPath(null);

    if (!studyPlanId) {
      setIsLoadingMetadata(false);
      return;
    }

    // Fire metadata + items in parallel; rec-path waits for metadata
    // because it needs the lesson_id from the response.
    fetchFeedItems(studyPlanId, keywords);

    const response = await api_get(
      `/feed/${studyPlanId}`,
      null,
      setError,
      90000,
    );
    if (response) {
      const feedData = response.data.items[0];
      setFeedMetadata(feedData);

      if (feedData?.current_position?.lesson_id) {
        fetchRecommendedPath(
          studyPlanId,
          feedData.current_position.lesson_id,
        );
      }
    }

    setIsLoadingMetadata(false);
  }, [fetchFeedItems, fetchRecommendedPath, setError]);

  const feedItems = feedMetadata
    ? {
      ...feedMetadata,
      items: feedItemsResp?.items || [],
      progress_by_video_id: feedItemsResp?.progress_by_video_id || {},
    }
    : null;
  const isRecPathReady = recommendedPath?.status === 'ready';
  const showFunLoader = isLoadingMetadata
    || (!feedItemsResp && !isRecPathReady);

  return {
    feedMetadata,
    setFeedMetadata,
    feedItemsResp,
    setFeedItemsResp,
    recommendedPath,
    setRecommendedPath,
    debugFeedData,
    setDebugFeedData,
    isLoadingMetadata,
    setIsLoadingMetadata,
    isLoadingItems,
    isRecommendedPathLoading,
    feedItems,
    showFunLoader,
    fetchFeed,
    fetchFeedItems,
    fetchRecommendedPath,
    fetchDebugFeedData,
    DEBUG_MODE_ENABLED,
  };
}
